/**
 * Unified Scan Engine Hook
 *
 * Core logic for the unified scanning system. Discovers [data-scan-item]
 * elements via MutationObserver, handles hierarchical / spatial navigation
 * modes, renders focus via a React overlay (no DOM classList), and manages
 * auto-timer for automatic scanning.
 *
 * Hierarchical scanning uses a 3-level model:
 *   Level 1 — zone:  main content vs sidebar
 *   Level 2 — row:   rows within the selected zone (sorted top-to-bottom)
 *   Level 3 — item:  individual items within the selected row
 *
 * When only one zone has scan items, the zone level is auto-skipped.
 * After any item click, the scanner resets to zone level so the user
 * starts fresh (standard AAC row/column scanning behavior).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  discoverScanItems,
  flattenItems,
  DiscoveredItems,
  ScanItem,
} from '@/lib/scanItemDiscovery';
import { getUserSettings } from '@/lib/userSettings';
import { isCaregiverRoute } from '@/lib/caregiverRoutes';
import { checkIsScanningMode } from '@/lib/scanningHelpers';
import { getExternalSwitchAction } from './useExternalSwitchInput';
import type { ScanningAction } from '@/lib/scanningSystem';
import { CursorStore } from '@/features/cursor/CursorStore';

// ── Types ────────────────────────────────────────────────────────────────────

type NavigationMode = 'hierarchical' | 'spatial';
type HierarchicalLevel = 'zone' | 'row' | 'item';

interface ScanCursor {
  zone: 'main' | 'sidebar';
  rowIndex: number;
  itemIndex: number;
  level: HierarchicalLevel;
}

export interface FocusOverlay {
  elements: HTMLElement[];
  type: 'item' | 'row' | 'zone';
}

export interface ScanEngineState {
  isActive: boolean;
  refreshItems: () => void;
  focusDefaultItem: () => boolean;
  focusOverlay: FocusOverlay | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInputCount(): number {
  const s = getUserSettings();
  if (s.controlMethod === 'external_switch') return s.externalSwitchCount || 1;
  if (s.controlMethod === 'mouth_gestures') return s.mouthGestures?.length || 1;
  if (s.controlMethod === 'eye_control' || s.controlMethod === 'eyecontrol')
    return s.eyeControlGestures?.length || 1;
  return 1;
}

function getNavigationMode(inputCount: number): NavigationMode {
  // Cell-by-cell mode with 5 gestures uses free-form spatial movement
  // (up/down/left/right moves to the nearest cell in that direction).
  // Row/column mode uses hierarchical scanning with directional action mapping.
  if (inputCount >= 5 && isCellByCell()) return 'spatial';
  return 'hierarchical';
}

function shouldAutoScan(): boolean {
  const s = getUserSettings();
  const count = getInputCount();
  return count <= 2 || s.scanControlMode === 'automatic';
}

function isCellByCell(): boolean {
  const s = getUserSettings();
  return s.scanMode === 'cell_by_cell';
}

/**
 * In cell-by-cell mode, merge sidebar rows into the end of main rows
 * so the scanner treats everything as one zone. This avoids an extra
 * zone-level click on every scan round. In row/column mode, keep
 * zones separate (current behavior).
 */
function mergeZonesForCellMode(items: DiscoveredItems): DiscoveredItems {
  if (!isCellByCell()) return items;
  if (items.sidebar.length === 0) return items;
  return {
    main: [...items.main, ...items.sidebar],
    sidebar: [],
  };
}

function getZoneRows(items: DiscoveredItems, zone: 'main' | 'sidebar'): ScanItem[][] {
  return zone === 'main' ? items.main : items.sidebar;
}

/**
 * Find the index of the first pagination row in a zone's rows.
 * Pagination rows have data-scan-row starting with "pagination" on an
 * ancestor element (e.g. "pagination-prev", "pagination-next").
 * Returns -1 if no pagination row exists.
 */
function findPaginationRowIndex(rows: ScanItem[][]): number {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.length === 0) continue;
    let el: HTMLElement | null = row[0].element;
    while (el) {
      const rowAttr = el.getAttribute('data-scan-row');
      if (rowAttr && rowAttr.startsWith('pagination')) return i;
      el = el.parentElement;
    }
  }
  return -1;
}

function makeCursor(): ScanCursor {
  return { zone: 'main', rowIndex: 0, itemIndex: 0, level: 'zone' };
}

/**
 * Find the position of a [data-scan-default] element in the discovered items.
 * Returns the position if found, null otherwise.
 */
function findDefaultPosition(items: DiscoveredItems): { zone: 'main' | 'sidebar'; rowIndex: number; itemIndex: number } | null {
  const defaultEl = document.querySelector<HTMLElement>('[data-scan-default]');
  if (!defaultEl) return null;
  // The default element might be a scan-item itself, or an ancestor
  // Find the actual scan-item element
  const scanEl = defaultEl.hasAttribute('data-scan-item')
    ? defaultEl
    : defaultEl.querySelector<HTMLElement>('[data-scan-item]');
  if (!scanEl) return null;
  return findItemPosition(items, scanEl);
}

/** Check if both zones have scannable items */
function hasBothZones(items: DiscoveredItems): boolean {
  const mainCount = items.main.reduce((s, r) => s + r.length, 0);
  const sidebarCount = items.sidebar.reduce((s, r) => s + r.length, 0);
  return mainCount > 0 && sidebarCount > 0;
}

/**
 * Click a scan item. If the element itself isn't a button/link,
 * find the first clickable descendant (the actual ActionCard, etc.)
 * and click that instead — .click() on a wrapper div won't propagate
 * down to child onClick handlers.
 */
/**
 * Click a scan item and return the cursor-target hint if present.
 */
function clickScanItem(el: HTMLElement): string | null {
  const cursorTarget = el.getAttribute('data-scan-cursor-target') || null;
  if (!el.isConnected) return cursorTarget;
  const tag = el.tagName;
  if (tag === 'BUTTON' || tag === 'A' || el.getAttribute('role') === 'button') {
    el.click();
    return cursorTarget;
  }
  const clickable = el.querySelector<HTMLElement>('button, a, [role="button"]');
  if (clickable) {
    clickable.click();
  } else {
    el.click();
  }
  return cursorTarget;
}

/**
 * Find (zone, rowIndex, itemIndex) of an element across both zones.
 */
function findItemPosition(
  items: DiscoveredItems,
  el: HTMLElement
): { zone: 'main' | 'sidebar'; rowIndex: number; itemIndex: number } | null {
  for (const zone of ['main', 'sidebar'] as const) {
    const rows = zone === 'main' ? items.main : items.sidebar;
    for (let r = 0; r < rows.length; r++) {
      for (let i = 0; i < rows[r].length; i++) {
        if (rows[r][i].element === el) return { zone, rowIndex: r, itemIndex: i };
      }
    }
  }
  return null;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useScanEngine(isRTL: boolean): ScanEngineState {
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);

  // React state for focus overlay — drives the overlay component in context
  const [focusOverlay, setFocusOverlay] = useState<FocusOverlay | null>(null);

  // All mutable engine state lives in refs (no re-renders on cursor move)
  const itemsRef = useRef<DiscoveredItems>({ sidebar: [], main: [] });
  const cursorRef = useRef<ScanCursor>(makeCursor());
  const isActiveRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemCycleCountRef = useRef(0);
  const sidebarCycleCountRef = useRef(0);
  // Timestamp of last successful cursor-target application. The deferred-default
  // mechanism is suppressed for 1.5s after a cursor-target lands to prevent it
  // from overriding the intended position (e.g. CommBoard utterance → speak button).
  const cursorTargetAppliedAtRef = useRef(0);
  // Tracks the last [data-scan-default] element that was applied by discover().
  // When the default element changes (e.g. categories → stations on the same
  // page), discover() automatically jumps cursor to the new default. This
  // handles async content (YouTube videos, Radio stations) and within-page
  // content changes (CommunicationBoard sub-boards).
  const lastDefaultElRef = useRef<HTMLElement | null>(null);
  // Tracks the last row-navigation direction so that when the user exits item
  // level via 'back' or up/down, the cursor advances to the adjacent row in
  // the direction they were traveling (spreadsheet item 4).
  const lastRowDirectionRef = useRef<'next' | 'prev'>('next');

  // Ref for setFocusOverlay so timer closures always call the latest version
  const setFocusOverlayRef = useRef(setFocusOverlay);
  setFocusOverlayRef.current = setFocusOverlay;

  // ── Focus rendering (React state, no DOM classList) ──────────────────────

  const renderFocus = useCallback(() => {
    if (!isActiveRef.current) {
      setFocusOverlayRef.current(null);
      return;
    }

    const items = itemsRef.current;
    const cursor = cursorRef.current;
    const mode = getNavigationMode(getInputCount());

    if (mode === 'hierarchical') {
      if (cursor.level === 'zone') {
        // Highlight the entire zone
        const zoneEl = document.querySelector<HTMLElement>(`[data-scan-zone="${cursor.zone}"]`);
        if (zoneEl) {
          setFocusOverlayRef.current({ elements: [zoneEl], type: 'zone' });
        } else {
          setFocusOverlayRef.current(null);
        }
      } else if (cursor.level === 'row') {
        const row = getZoneRows(items, cursor.zone)[cursor.rowIndex];
        if (row && row.length > 0) {
          setFocusOverlayRef.current({ elements: row.map(i => i.element), type: 'row' });
          row[0].element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          setFocusOverlayRef.current(null);
        }
      } else {
        // item level
        const item = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex];
        if (item) {
          setFocusOverlayRef.current({ elements: [item.element], type: 'item' });
          item.element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          setFocusOverlayRef.current(null);
        }
      }
    } else {
      // spatial — always show single item
      const item = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex];
      if (item) {
        setFocusOverlayRef.current({ elements: [item.element], type: 'item' });
        item.element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        setFocusOverlayRef.current(null);
      }
    }
  }, []);

  // ── Post-click reset ────────────────────────────────────────────────────
  // After clicking a scan item, reset to zone level so user starts fresh.
  // This is standard AAC behavior — the page content may change after a click.

  const resetAfterClickRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCursorTargetRef = useRef<string | null>(null);

  const resetToZone = useCallback(() => {
    const cursor = cursorRef.current;
    cursor.zone = 'main';
    cursor.rowIndex = 0;
    cursor.itemIndex = 0;
    itemCycleCountRef.current = 0;
    // Always land at row level after a click — removes the whole-content zone frame
    // that previously appeared after every selection. Zone level is still reachable
    // via explicit 'back' from row level in multi-gesture modes.
    cursor.level = 'row';
  }, []);

  /**
   * Move cursor to a specific element by finding its position in discovered items.
   * Returns true if the element was found and cursor was moved.
   */
  const moveCursorToElement = useCallback((el: HTMLElement): boolean => {
    const items = itemsRef.current;
    const pos = findItemPosition(items, el);
    if (!pos) return false;
    const cursor = cursorRef.current;
    cursor.zone = pos.zone;
    cursor.rowIndex = pos.rowIndex;
    cursor.itemIndex = pos.itemIndex;
    // Allow the target element to declare what level the cursor should land on.
    // data-scan-cursor-level="row" → land at row level (user selects from there).
    // Omitted or any other value → item level (direct focus on the element).
    const declaredLevel = el.getAttribute('data-scan-cursor-level');
    cursor.level = declaredLevel === 'row' ? 'row' : 'item';
    itemCycleCountRef.current = 0;
    return true;
  }, []);

  const focusDefaultItem = useCallback((): boolean => {
    // In auto-scan mode, don't jump to the default position — the scanner
    // starts at row 0 and cycles through all rows. Only manual modes need
    // the default-position jump.
    if (shouldAutoScan()) return false;

    const rawItems = discoverScanItems(document.documentElement, isRTL);
    const items = mergeZonesForCellMode(rawItems);
    itemsRef.current = items;

    const defaultEl = document.querySelector<HTMLElement>('[data-scan-default]');
    const defaultPos = defaultEl ? findItemPosition(items, defaultEl) : null;
    if (!defaultEl || !defaultPos) return false;

    const cursor = cursorRef.current;
    cursor.zone = defaultPos.zone;
    cursor.rowIndex = defaultPos.rowIndex;
    cursor.itemIndex = defaultPos.itemIndex;
    // Respect data-scan-cursor-level on the default element: "row" keeps
    // the cursor at row level so the user scans the row before drilling in.
    const declaredLevel = defaultEl.getAttribute('data-scan-cursor-level');
    cursor.level = declaredLevel === 'row' ? 'row' : 'item';
    itemCycleCountRef.current = 0;
    lastDefaultElRef.current = defaultEl;

    const rect = defaultEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    renderFocus();
    return true;
  }, [isRTL, renderFocus]);

  const scheduleResetAfterClick = useCallback(() => {
    if (resetAfterClickRef.current) clearTimeout(resetAfterClickRef.current);
    resetAfterClickRef.current = setTimeout(() => {
      // Check if the clicked element had a cursor target
      const targetId = pendingCursorTargetRef.current;
      // Do NOT clear pendingCursorTargetRef here — only clear it after
      // successfully moving to the target. For async content (e.g. YouTube tabs
      // where videos load after the 300ms window), discover() will apply the
      // jump when the target element eventually appears in the DOM.

      let moved = false;
      if (targetId) {
        // Re-discover items first (content may have changed after click)
        const rawItems = discoverScanItems(document.documentElement, isRTL);
        const items = mergeZonesForCellMode(rawItems);
        itemsRef.current = items;

        // Find the target element
        const targetEl = document.querySelector<HTMLElement>(`[data-scan-cursor-id="${targetId}"]`);
        if (targetEl) {
          moved = moveCursorToElement(targetEl);
          // Sync visual cursor (VirtualCursor dot) to the target element's position
          if (moved) {
            pendingCursorTargetRef.current = null; // Applied — clear
            cursorTargetAppliedAtRef.current = Date.now(); // Suppress deferred-default
            lastDefaultElRef.current = document.querySelector<HTMLElement>('[data-scan-default]');
            const rect = targetEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
            }
          }
        }
      }

      if (!moved) {
        if (isCellByCell()) {
          // In spatial cell-by-cell mode, never reset to zone level — it jumps
          // the cursor to the first scan item (often a page tab) unexpectedly.
          // Instead, move to the data-scan-default element if present, or
          // leave the cursor where it is.
          const cursor = cursorRef.current;
          const defaultEl = document.querySelector<HTMLElement>('[data-scan-default]');
          const defaultPos = defaultEl ? findItemPosition(itemsRef.current, defaultEl) : null;
          if (defaultPos) {
            cursor.zone = defaultPos.zone;
            cursor.rowIndex = defaultPos.rowIndex;
            cursor.itemIndex = defaultPos.itemIndex;
            cursor.level = 'item';
            const rect = defaultEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
            }
          }
          // If no default, cursor stays at current item — nothing to do
        } else {
          // Reset to row 0 so something sane is shown while content loads.
          // If targetId is set, pendingCursorTargetRef is still live so
          // discover() will jump to the target when content appears.
          resetToZone();
        }
      }

      renderFocus();
      // Restart timer
      if (isActiveRef.current && shouldAutoScan()) {
        startTimerRef.current();
      }
    }, 300);
  }, [resetToZone, renderFocus, isRTL, moveCursorToElement]);

  // ── Navigation handlers ────────────────────────────────────────────────

  const handleHierarchicalAction = useCallback((action: ScanningAction, fromAutoScan = false) => {
    const items = itemsRef.current;
    const cursor = cursorRef.current;
    const maxCycles = getUserSettings().cellLevelMaxCycles || 2;
    const bothZones = hasBothZones(items);
    const cellMode = isCellByCell();

    // ── Directional mapping for 5-gesture row/column mode ─────────────────
    // Row level  — up/down navigates rows; left/right exits to sibling zone
    // Item level — left/right navigates items (RTL-aware); up/down exits to row scan

    switch (cursor.level) {
      case 'zone': {
        // All directional actions at zone level: switch between zones.
        // Only 'select' enters the zone (drills down to row/item level).
        // This prevents left/right from accidentally entering the sidebar
        // in 5-gesture row/column mode (where left/right are directional).
        if (action === 'next' || action === 'prev' || action === 'up' || action === 'down'
            || action === 'left' || action === 'right') {
          cursor.zone = cursor.zone === 'main' ? 'sidebar' : 'main';
          const rows = getZoneRows(items, cursor.zone);
          if (rows.length === 0) {
            cursor.zone = cursor.zone === 'main' ? 'sidebar' : 'main';
          }
        } else if (action === 'select') {
          // select: enter zone at row level
          const rows = getZoneRows(items, cursor.zone);
          if (rows.length > 0) {
            cursor.rowIndex = 0;
            if (cellMode || rows.length === 1) {
              const row = rows[0];
              if (row.length === 1 && !cellMode) {
                // Single item in row — click it directly (1 press = 1 action)
                pendingCursorTargetRef.current = clickScanItem(row[0].element);
                scheduleResetAfterClick();
              } else {
                cursor.level = 'item';
                cursor.itemIndex = 0;
                itemCycleCountRef.current = 0;
              }
            } else {
              cursor.level = 'row';
            }
          }
        } else if (action === 'cancel' || action === 'back') {
          cursor.zone = 'main';
        }
        break;
      }

      case 'row': {
        const rows = getZoneRows(items, cursor.zone);
        if (rows.length === 0) { cursor.level = 'zone'; break; }

        if (action === 'next' || action === 'down') {
          // Move to next row
          cursor.rowIndex = (cursor.rowIndex + 1) % rows.length;
          lastRowDirectionRef.current = 'next';
        } else if (action === 'prev' || action === 'up') {
          // Move to previous row
          cursor.rowIndex = (cursor.rowIndex - 1 + rows.length) % rows.length;
          lastRowDirectionRef.current = 'prev';
        } else if (action === 'left' || action === 'right') {
          // In RTL: right = toward sidebar, left = away from sidebar ("free" direction)
          // In LTR: left = toward sidebar, right = away from sidebar ("free" direction)
          const towardSidebar = isRTL ? action === 'right' : action === 'left';
          // From main, "toward sidebar" switches zone. From sidebar, "away from
          // sidebar" (toward main content) switches zone. This ensures pressing
          // left/right toward the OTHER zone always navigates there.
          const wantsOtherZone = cursor.zone === 'main' ? towardSidebar : !towardSidebar;
          if (wantsOtherZone && bothZones) {
            // Switch to sibling zone.
            // When moving INTO the sidebar, show zone-level frame first (sidebar
            // highlighted as a group) so the user sees it as a unit before
            // selecting into individual rows. When returning to main, land at row
            // level directly (no zone frame for main content).
            const targetZone = cursor.zone === 'main' ? 'sidebar' : 'main';
            const targetRows = getZoneRows(items, targetZone);
            if (targetRows.length > 0) {
              cursor.zone = targetZone;
              cursor.rowIndex = 0;
              cursor.level = targetZone === 'sidebar' ? 'zone' : 'row';
            }
          } else if (!wantsOtherZone) {
            // Free direction (staying in current zone) — jump to pagination row
            // if one exists; otherwise wrap back to row 0.
            const paginationIdx = findPaginationRowIndex(rows);
            cursor.rowIndex = paginationIdx >= 0 ? paginationIdx : 0;
          }
        } else if (action === 'select') {
          const row = rows[cursor.rowIndex];
          if (row && row.length > 0) {
            if (row.length === 1) {
              // Single item in row — click it directly (1 press = 1 action).
              // No need to enter item level for a single element.
              pendingCursorTargetRef.current = clickScanItem(row[0].element);
              scheduleResetAfterClick();
            } else {
              cursor.level = 'item';
              cursor.itemIndex = 0;
              itemCycleCountRef.current = 0;
            }
          }
        } else if (action === 'back' || action === 'cancel') {
          if (cursor.zone === 'sidebar' && bothZones) {
            // Back from sidebar row scan → exit to main zone row 0
            cursor.zone = 'main';
            cursor.rowIndex = 0;
            cursor.itemIndex = 0;
            cursor.level = 'row';
            sidebarCycleCountRef.current = 0;
          } else if (bothZones) {
            cursor.level = 'zone';
          } else {
            cursor.rowIndex = 0;
          }
        }
        break;
      }

      case 'item': {
        const rows = getZoneRows(items, cursor.zone);
        const row = rows[cursor.rowIndex];
        if (!row || row.length === 0) { cursor.level = cellMode ? 'zone' : 'row'; break; }

        // Map left/right to next/prev accounting for RTL
        const isNextDir = action === 'next' || (isRTL ? action === 'left' : action === 'right');
        const isPrevDir = action === 'prev' || (isRTL ? action === 'right' : action === 'left');

        if (isNextDir) {
          cursor.itemIndex++;
          if (cursor.itemIndex >= row.length) {
            if (cellMode) {
              if (fromAutoScan) {
                // Auto-scan: advance top-to-bottom through rows
                cursor.rowIndex++;
                if (cursor.rowIndex >= rows.length) {
                  cursor.rowIndex = 0;
                  itemCycleCountRef.current++;
                  if (itemCycleCountRef.current >= maxCycles && getInputCount() <= 3) {
                    cursor.level = bothZones ? 'zone' : 'item';
                    cursor.rowIndex = 0;
                    cursor.itemIndex = 0;
                    itemCycleCountRef.current = 0;
                    break;
                  }
                }
                cursor.itemIndex = 0;
              } else {
                // Manual sideways: wrap within the same row
                cursor.itemIndex = 0;
              }
            } else {
              cursor.itemIndex = 0;
              itemCycleCountRef.current++;
              if (itemCycleCountRef.current >= maxCycles && getInputCount() <= 3) {
                cursor.level = 'row';
                itemCycleCountRef.current = 0;
              }
            }
          }
        } else if (isPrevDir) {
          cursor.itemIndex--;
          if (cursor.itemIndex < 0) {
            if (cellMode) {
              if (fromAutoScan) {
                // Auto-scan: wrap to previous row
                cursor.rowIndex--;
                if (cursor.rowIndex < 0) {
                  cursor.rowIndex = rows.length - 1;
                }
                const prevRow = rows[cursor.rowIndex];
                cursor.itemIndex = prevRow ? prevRow.length - 1 : 0;
              } else {
                // Manual sideways: wrap within the same row
                cursor.itemIndex = row.length - 1;
              }
            } else {
              cursor.itemIndex = row.length - 1;
            }
          }
        } else if (action === 'up' || action === 'down') {
          // Exit item level → return to row level and advance one row in the pressed direction
          const dir = action === 'down' ? 'next' : 'prev';
          lastRowDirectionRef.current = dir;
          cursor.level = cellMode ? 'zone' : 'row';
          itemCycleCountRef.current = 0;
          if (!cellMode) {
            if (dir === 'next') {
              cursor.rowIndex = (cursor.rowIndex + 1) % rows.length;
            } else {
              cursor.rowIndex = (cursor.rowIndex - 1 + rows.length) % rows.length;
            }
            cursor.itemIndex = 0;
          }
        } else if (action === 'select') {
          const el = row[cursor.itemIndex]?.element;
          if (el) {
            pendingCursorTargetRef.current = clickScanItem(el);
            scheduleResetAfterClick();
          }
        } else if (action === 'back' || action === 'cancel') {
          // Exit item level → return to row level and advance one row in the last traveled direction
          cursor.level = cellMode ? 'zone' : 'row';
          itemCycleCountRef.current = 0;
          if (!cellMode) {
            const dir = lastRowDirectionRef.current;
            if (dir === 'next') {
              cursor.rowIndex = (cursor.rowIndex + 1) % rows.length;
            } else {
              cursor.rowIndex = (cursor.rowIndex - 1 + rows.length) % rows.length;
            }
            cursor.itemIndex = 0;
          }
        }
        break;
      }
    }
  }, [scheduleResetAfterClick, isRTL]);

  const handleSpatialAction = useCallback((action: ScanningAction) => {
    const items = itemsRef.current;
    const cursor = cursorRef.current;

    if (action === 'select') {
      const el = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex]?.element;
      if (el) {
        pendingCursorTargetRef.current = clickScanItem(el);
        scheduleResetAfterClick();
      }
      return;
    }

    // Map action to physical screen direction
    let direction: 'up' | 'down' | 'left' | 'right' | null = null;
    if (action === 'up') direction = 'up';
    else if (action === 'down') direction = 'down';
    else if (action === 'left') direction = 'left';
    else if (action === 'right') direction = 'right';
    else if (action === 'next') direction = isRTL ? 'left' : 'right';
    else if (action === 'prev') direction = isRTL ? 'right' : 'left';

    if (!direction) return;

    const currentItem = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex];
    if (!currentItem) return;

    const cx = currentItem.rect.left + currentItem.rect.width / 2;
    const cy = currentItem.rect.top + currentItem.rect.height / 2;

    // ── Spatial navigation: CSS Spatial Navigation beam algorithm ──────────
    //
    // For every direction, we look for items in two passes:
    //
    // Pass 1 — BEAM: items whose cross-axis bounding box overlaps the current
    //   item's cross-axis box (like drawing a beam extending from the item
    //   in the navigation direction). These are items "directly" in the way.
    //   Among beam items: nearest nav-axis distance first, then closest
    //   cross-axis within the same row/column.
    //
    // Pass 2 — ANGULAR FALLBACK: if no beam item exists, pick the item most
    //   directly in the direction by angle, breaking ties by total distance.
    //
    // Zone rule: UP/DOWN navigation NEVER crosses zone boundaries (main ↔
    //   sidebar). Sidebar items are only reachable via LEFT/RIGHT. This
    //   prevents the sidebar navigation panel from intercepting vertical
    //   moves meant for keyboard rows.

    const isVertical = direction === 'up' || direction === 'down';
    const TOLERANCE = 5;

    // Center coords helpers
    const itemCX = (item: ScanItem) => item.rect.left + item.rect.width / 2;
    const itemCY = (item: ScanItem) => item.rect.top + item.rect.height / 2;

    // Navigation-axis distance (center to center)
    const navDist = (item: ScanItem) =>
      isVertical ? Math.abs(itemCY(item) - cy) : Math.abs(itemCX(item) - cx);

    // Cross-axis distance (center to center)
    const crossDist = (item: ScanItem) =>
      isVertical ? Math.abs(itemCX(item) - cx) : Math.abs(itemCY(item) - cy);

    // Is item in the correct half-plane?
    const inDirection = (item: ScanItem): boolean => {
      switch (direction) {
        case 'up':    return itemCY(item) < cy - TOLERANCE;
        case 'down':  return itemCY(item) > cy + TOLERANCE;
        case 'left':  return itemCX(item) < cx - TOLERANCE;
        case 'right': return itemCX(item) > cx + TOLERANCE;
      }
    };

    // Does item's cross-axis bounding box overlap the current item's?
    const inBeam = (item: ScanItem): boolean => {
      const ir = item.rect;
      const cr = currentItem.rect;
      if (isVertical) return ir.left < cr.right && ir.right > cr.left;
      return ir.top < cr.bottom && ir.bottom > cr.top;
    };

    // Candidates: items in the correct direction, respecting zone boundaries
    const candidates = flattenItems(items).filter(item => {
      if (item.element === currentItem.element) return false;
      if (!inDirection(item)) return false;
      // UP/DOWN stays within the same zone (prevents sidebar interception)
      if (isVertical && item.zone !== currentItem.zone) return false;
      return true;
    });

    // Pick best from a pool: nearest nav-axis row first, then closest cross-axis
    const pickBest = (pool: ScanItem[]): ScanItem | null => {
      if (pool.length === 0) return null;
      pool.sort((a, b) => {
        const ndA = navDist(a), ndB = navDist(b);
        // Items within 20% of each other are on the "same row" — prefer cross-axis
        const rowTol = Math.min(ndA, ndB) * 0.25 + 15;
        if (Math.abs(ndA - ndB) > rowTol) return ndA - ndB;
        return crossDist(a) - crossDist(b);
      });
      return pool[0];
    };

    const beamItems = candidates.filter(inBeam);
    let bestItem: ScanItem | null = pickBest(beamItems);

    if (!bestItem && candidates.length > 0) {
      // Angular fallback: sort by angle from direction, then total distance
      candidates.sort((a, b) => {
        const ndA = navDist(a), ndB = navDist(b);
        const cdA = crossDist(a), cdB = crossDist(b);
        const angleA = Math.atan2(cdA, ndA);
        const angleB = Math.atan2(cdB, ndB);
        if (Math.abs(angleA - angleB) > 0.15) return angleA - angleB;
        return Math.hypot(ndA, cdA) - Math.hypot(ndB, cdB);
      });
      bestItem = candidates[0];
    }

    if (bestItem) {
      const pos = findItemPosition(items, bestItem.element);
      if (pos) {
        cursor.zone = pos.zone;
        cursor.rowIndex = pos.rowIndex;
        cursor.itemIndex = pos.itemIndex;
      }
    }
  }, [isRTL, scheduleResetAfterClick]);

  // ── Main action dispatcher ─────────────────────────────────────────────

  const handleAction = useCallback((action: ScanningAction) => {
    if (!isActiveRef.current) return;

    const mode = getNavigationMode(getInputCount());
    if (mode === 'spatial') {
      handleSpatialAction(action);
    } else {
      handleHierarchicalAction(action);
    }

    renderFocus();
  }, [handleHierarchicalAction, handleSpatialAction, renderFocus]);

  // ── Auto-timer ─────────────────────────────────────────────────────────

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    if (!isActiveRef.current) return;

    const mode = getNavigationMode(getInputCount());
    if (mode === 'spatial') return; // no auto-scan for spatial (cell-by-cell)
    if (!shouldAutoScan()) return;

    const speed = (getUserSettings().scanSpeed || 3) * 1000;

    timerRef.current = setInterval(() => {
      if (!isActiveRef.current) { stopTimer(); return; }

      const items = itemsRef.current;
      const cursor = cursorRef.current;
      const cellMode = isCellByCell();

      if (cursor.level === 'zone') {
        const bothZones = hasBothZones(items);
        if (bothZones && cursor.zone === 'sidebar') {
          // Sidebar was shown as a group for one tick (single-item duration).
          // User did not select → skip drill-down and return to main row 0
          // to continue normal row scanning.
          cursor.zone = 'main';
          cursor.rowIndex = 0;
          cursor.itemIndex = 0;
          cursor.level = 'row';
          sidebarCycleCountRef.current = 0;
        } else if (bothZones && cursor.zone === 'main') {
          // Main zone frame visible (after user pressed "back"). On the next
          // tick, advance to sidebar zone so the user can see and select it.
          cursor.zone = 'sidebar';
          // Stay at zone level — next tick will return to main if no selection
        } else {
          // Single zone: drill down into row/item level
          handleHierarchicalAction('select');
        }
      } else if (cursor.level === 'row') {
        const isHomeScreen = window.location.pathname === '/';
        const isHomeScreenAutoItems = getInputCount() <= 3 && isHomeScreen;
        if (cellMode || isHomeScreenAutoItems) {
          handleHierarchicalAction('select');
        } else {
          const rows = getZoneRows(items, cursor.zone);
          const bothZones = hasBothZones(items);

          if (rows.length === 1 && !bothZones) {
            // Only 1 row and no sidebar — auto-select into items
            handleHierarchicalAction('select');
          } else if (cursor.zone === 'sidebar') {
            // User actively selected sidebar group → cell-by-cell through
            // sidebar rows. After N cycles (cellLevelMaxCycles, default 2)
            // without selection, exit back to main row 0.
            const sidebarRows = getZoneRows(items, 'sidebar');
            const maxSidebarCycles = getUserSettings().cellLevelMaxCycles || 2;
            if (cursor.rowIndex >= sidebarRows.length - 1) {
              sidebarCycleCountRef.current++;
              if (sidebarCycleCountRef.current >= maxSidebarCycles && getInputCount() <= 3) {
                cursor.zone = 'main';
                cursor.rowIndex = 0;
                cursor.itemIndex = 0;
                cursor.level = 'row';
                sidebarCycleCountRef.current = 0;
              } else {
                cursor.rowIndex = 0;
              }
            } else {
              handleHierarchicalAction('next');
            }
          } else if (cursor.zone === 'main' && bothZones && !isHomeScreen
                     && cursor.rowIndex >= rows.length - 1) {
            // Cursor is on the last row in main zone (including pagination
            // rows at the end). Next step: show sidebar as a group.
            cursor.zone = 'sidebar';
            cursor.rowIndex = 0;
            cursor.level = 'zone';
            sidebarCycleCountRef.current = 0;
          } else {
            handleHierarchicalAction('next');
          }
        }
      } else {
        // item level — always advance (fromAutoScan=true: allow row-wrap for cell-by-cell)
        handleHierarchicalAction('next', true);
      }

      renderFocus();
    }, speed);
  }, [stopTimer, renderFocus, handleHierarchicalAction]);

  // ── Discovery ──────────────────────────────────────────────────────────

  const discover = useCallback(() => {
    const pathname = window.location.pathname;
    const currentSettings = getUserSettings();
    const isScanningEnabled =
      !isCaregiverRoute(pathname) &&
      checkIsScanningMode(null, currentSettings.controlMethod);

    // Save anchor element BEFORE rediscovery so we can restore cursor position
    // by element identity (not index). This prevents cursor jumps when rows
    // appear/disappear (e.g. suggestions row in YouTube keyboard).
    const prevCursor = cursorRef.current;
    let anchorElement: HTMLElement | null = null;
    if (prevCursor.level !== 'zone') {
      const prevRows = getZoneRows(itemsRef.current, prevCursor.zone);
      if (prevCursor.level === 'row') {
        // Any element in the focused row works as anchor
        anchorElement = prevRows[prevCursor.rowIndex]?.[0]?.element ?? null;
      } else {
        anchorElement = prevRows[prevCursor.rowIndex]?.[prevCursor.itemIndex]?.element ?? null;
      }
    }

    const rawItems = discoverScanItems(document.documentElement, isRTL);
    const items = mergeZonesForCellMode(rawItems);
    itemsRef.current = items;

    const mainItemCount = items.main.reduce((sum, row) => sum + row.length, 0);
    const sidebarItemCount = items.sidebar.reduce((sum, row) => sum + row.length, 0);
    const hasMainItems = mainItemCount > 0;
    const nowActive = hasMainItems && isScanningEnabled;
    const wasActive = isActiveRef.current;
    isActiveRef.current = nowActive;

    if (nowActive !== wasActive) {
      setIsActive(nowActive);
    }

    if (nowActive) {
      const cursor = cursorRef.current;

      // If there is a pending cursor-target from a tab/mode click (async content):
      // apply it as soon as the target element appears in the DOM.
      const pendingTarget = pendingCursorTargetRef.current;
      if (pendingTarget) {
        const targetEl = document.querySelector<HTMLElement>(`[data-scan-cursor-id="${pendingTarget}"]`);
        if (targetEl) {
          const moved = moveCursorToElement(targetEl);
          if (moved) {
            pendingCursorTargetRef.current = null;
            lastDefaultElRef.current = document.querySelector<HTMLElement>('[data-scan-default]'); // Cursor target applied
            cursorTargetAppliedAtRef.current = Date.now();
            // Cancel any pending reset-after-click — cursor is already positioned.
            if (resetAfterClickRef.current) {
              clearTimeout(resetAfterClickRef.current);
              resetAfterClickRef.current = null;
            }
            const rect = targetEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
            }
            renderFocus();
            if (!timerRef.current) {
              startTimer();
            }
            return;
          }
        }
      }

      // If cursor is at zone level and this is the scanner's first activation
      // (wasActive=false), auto-advance to row level. When wasActive=true the
      // user has deliberately navigated here via 'back' — keep at zone level.

      if (cursor.level === 'zone' && cursor.rowIndex === 0 && cursor.itemIndex === 0 && !wasActive) {
        const mode = getNavigationMode(getInputCount());
        const autoScan = shouldAutoScan();

        if (autoScan) {
          // Auto-scan modes: start from row 0 (topmost row — tabs, action bar).
          // The auto-scan timer cycles through ALL rows top-to-bottom.
          cursor.level = 'row';
          cursor.rowIndex = 0;
          lastDefaultElRef.current = document.querySelector<HTMLElement>('[data-scan-default]');
        } else {
          // Manual modes (4/5 gesture_controlled): start at the data-scan-default
          // position (first content row), skipping top rows like tabs/action bar.
          const defaultPos = findDefaultPosition(items);
          if (defaultPos) {
            cursor.zone = defaultPos.zone;
            cursor.rowIndex = defaultPos.rowIndex;
            cursor.itemIndex = defaultPos.itemIndex;
            cursor.level = mode === 'spatial' ? 'item' : 'row';
            lastDefaultElRef.current = document.querySelector<HTMLElement>('[data-scan-default]');
          } else {
            cursor.level = 'row';
            cursor.rowIndex = 0;
          }
        }
      } else if (cursor.level !== 'zone' && anchorElement) {
        // Try to restore cursor to the same element/row after content changes.
        // This prevents the cursor from jumping when rows are added or removed.
        const pos = findItemPosition(items, anchorElement);
        if (pos) {
          cursor.zone = pos.zone;
          cursor.rowIndex = pos.rowIndex;
          if (cursor.level === 'item') cursor.itemIndex = pos.itemIndex;

        } else {
          const defaultEl = document.querySelector<HTMLElement>('[data-scan-default]');
          const defaultPos = defaultEl ? findItemPosition(items, defaultEl) : null;

          if (defaultPos) {
            cursor.zone = defaultPos.zone;
            cursor.rowIndex = defaultPos.rowIndex;
            cursor.itemIndex = defaultPos.itemIndex;
            // Respect the data-scan-cursor-level hint on the default element
            // (e.g. first station card declares row level so the user scans
            // the station row rather than landing directly on one card).
            const declaredLevel = defaultEl!.getAttribute('data-scan-cursor-level');
            cursor.level = declaredLevel === 'row' ? 'row' : 'item';
            lastDefaultElRef.current = defaultEl;
            // Sync visual cursor to the default element
            const rect = defaultEl!.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
            }
          } else {
            // Fall back: clamp to valid bounds and reset level.
            // Land at row level to avoid the zone-frame flash when view changes
            // (e.g. CommunicationBoard category switch, keyboard suggestions update).
            cursor.level = 'row';
            cursor.rowIndex = 0;
            cursor.itemIndex = 0;
            const cursorRows = getZoneRows(items, cursor.zone);
            if (cursorRows.length > 0) {
              const row = cursorRows[0];
              if (!row || row.length === 0) cursor.rowIndex = 0;
            }
          }
        }
      } else if (cursor.level !== 'zone') {
        // No anchor — clamp to valid bounds
        const cursorRows = getZoneRows(items, cursor.zone);
        if (cursorRows.length === 0) {
          cursor.level = 'zone';
        } else {
          if (cursor.rowIndex >= cursorRows.length) cursor.rowIndex = 0;
          const row = cursorRows[cursor.rowIndex];
          if (row && cursor.itemIndex >= row.length) cursor.itemIndex = 0;
        }
      }

      // Deferred default: when the [data-scan-default] element changes (either
      // because async content loaded, or because within-page navigation changed
      // the content — e.g. Radio categories → stations), jump cursor to the new
      // default. Only applies to MANUAL modes — in auto-scan mode, the scanner
      // starts at row 0 and cycles through all rows, so the default position
      // should not override the current cursor.
      {
        const defaultEl = document.querySelector<HTMLElement>('[data-scan-default]');
        const recentCursorTarget = Date.now() - cursorTargetAppliedAtRef.current < 1500;
        const autoScanActive = shouldAutoScan();
        if (defaultEl && defaultEl !== lastDefaultElRef.current && !recentCursorTarget && !autoScanActive) {
          const defaultPos = findItemPosition(items, defaultEl);
          if (defaultPos) {
            cursor.zone = defaultPos.zone;
            cursor.rowIndex = defaultPos.rowIndex;
            cursor.itemIndex = defaultPos.itemIndex;
            const mode = getNavigationMode(getInputCount());
            const declaredLevel = defaultEl.getAttribute('data-scan-cursor-level');
            cursor.level = mode === 'spatial' ? 'item' : (declaredLevel === 'row' ? 'row' : 'row');
            lastDefaultElRef.current = defaultEl;
            const rect = defaultEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
            }
          }
        }
      }


      renderFocus();
      if (!timerRef.current) {
        startTimer();
      }
    } else {
      setFocusOverlayRef.current(null);
      stopTimer();
    }
  }, [isRTL, renderFocus, startTimer, stopTimer, moveCursorToElement]);

  // Second-pass timeout ref — ensures all items are found after animations complete
  const secondPassRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedDiscover = useCallback(() => {
    if (discoverTimeoutRef.current) clearTimeout(discoverTimeoutRef.current);
    if (secondPassRef.current) clearTimeout(secondPassRef.current);

    discoverTimeoutRef.current = setTimeout(() => {
      discover();
      // Schedule a second discovery pass after Framer Motion staggered
      // animations complete. Items with staggered delays (index * 30ms)
      // may still be at opacity: 0 during the first pass and get filtered
      // out by isVisible, causing incomplete row detection.
      secondPassRef.current = setTimeout(discover, 600);
    }, 250);
  }, [discover]);

  // ── Event listeners ────────────────────────────────────────────────────

  const handleActionRef = useRef(handleAction);
  handleActionRef.current = handleAction;
  const startTimerRef = useRef(startTimer);
  startTimerRef.current = startTimer;

  // Listen to scanningAction events (dispatched by useEyeControlInput)
  useEffect(() => {
    const handler = (e: Event) => {
      if (!isActiveRef.current) return;
      const action = (e as CustomEvent<{ action: ScanningAction }>).detail.action;

      if (shouldAutoScan()) {
        startTimerRef.current();
      }

      handleActionRef.current(action);
    };

    window.addEventListener('scanningAction', handler);
    return () => window.removeEventListener('scanningAction', handler);
  }, []);

  // Listen to keyboard events for external switch input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isActiveRef.current) return;

      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      const settings = getUserSettings();
      if (settings.controlMethod !== 'external_switch') return;

      const rawAction = getExternalSwitchAction(e);
      if (!rawAction) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const action: ScanningAction = rawAction === 'previous' ? 'prev' : rawAction as ScanningAction;

      if (shouldAutoScan()) {
        startTimerRef.current();
      }

      handleActionRef.current(action);
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, []);

  // Listen to native mouse clicks on scan items so cursor targeting works for
  // mouse/touch users the same way it does for scan/keyboard/eye-control users.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const scanEl = target.closest<HTMLElement>('[data-scan-item]');
      if (!scanEl) return;
      const cursorTarget = scanEl.getAttribute('data-scan-cursor-target');
      pendingCursorTargetRef.current = cursorTarget;
      scheduleResetAfterClick();
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [scheduleResetAfterClick]);

  // ── MutationObserver ───────────────────────────────────────────────────

  useEffect(() => {
    debouncedDiscover();

    const mutationObserver = new MutationObserver(debouncedDiscover);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-scan-item', 'hidden', 'disabled', 'aria-disabled'],
    });

    const resizeObserver = new ResizeObserver(debouncedDiscover);
    resizeObserver.observe(document.body);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      if (discoverTimeoutRef.current) clearTimeout(discoverTimeoutRef.current);
      if (secondPassRef.current) clearTimeout(secondPassRef.current);
    };
  }, [debouncedDiscover]);

  // ── Route change reset ─────────────────────────────────────────────────

  useEffect(() => {
    cursorRef.current = makeCursor();
    itemCycleCountRef.current = 0;
    pendingCursorTargetRef.current = null; // Clear stale cursor targets on route change
    lastDefaultElRef.current = null; // New page — re-evaluate data-scan-default
    cursorTargetAppliedAtRef.current = 0; // Clear cooldown
    // Reset active state so the next discover() treats this as a fresh activation
    // and correctly auto-advances from zone level to row level.
    isActiveRef.current = false;
    if (resetAfterClickRef.current) clearTimeout(resetAfterClickRef.current);
    if (secondPassRef.current) clearTimeout(secondPassRef.current);
    setFocusOverlay(null);
    stopTimer();

    const timer = setTimeout(discover, 200);
    return () => clearTimeout(timer);
  }, [location.pathname, stopTimer, discover]);

  // ── Settings change listener ───────────────────────────────────────────

  useEffect(() => {
    const handler = () => {
      stopTimer();
      debouncedDiscover();
    };
    window.addEventListener('userSettingsChanged', handler);
    window.addEventListener('controlMethodChanged', handler);
    return () => {
      window.removeEventListener('userSettingsChanged', handler);
      window.removeEventListener('controlMethodChanged', handler);
    };
  }, [debouncedDiscover, stopTimer]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      stopTimer();
      if (resetAfterClickRef.current) clearTimeout(resetAfterClickRef.current);
      if (secondPassRef.current) clearTimeout(secondPassRef.current);
    };
  }, [stopTimer]);

  return { isActive, refreshItems: discover, focusDefaultItem, focusOverlay };
}
